import type { JSX } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { api } from '@/lib/api-client';
import { KNOWLEDGE_BASE, isFeatureEnabled } from '@/auth/features';
import { formatDate } from '@/lib/utils';

export function UserDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const overview = useQuery({
    queryKey: [`/api/admin/users/${id}/overview`],
    queryFn: () =>
      api.get<{
        user: { name: string; email: string; jobTitle?: string; department?: string; lastLoginAt?: string };
        segments: { segmentId: string; segmentName: string; source: string }[];
        effective: {
          models: { id: string; name: string }[];
          skills: { id: string; name: string }[];
          mcps: { id: string; name: string }[];
          knowledgeBases: { id: string; name: string }[];
        };
        recentUsage: { id: string; inputTokens: number; outputTokens: number; occurredAt: string }[];
      }>(`/api/admin/users/${id}/overview`),
    enabled: !!id,
  });

  if (overview.isLoading) return <p className="text-slate-500">Betöltés…</p>;
  if (overview.isError) return <p className="text-rose-600">Hiba: {String(overview.error)}</p>;
  const o = overview.data!;
  const effectiveCategories: { label: string; items: { id: string; name: string }[] }[] = [
    { label: 'Models', items: o.effective.models },
    { label: 'Agents', items: o.effective.skills },
    { label: 'Integrations', items: o.effective.mcps },
    ...(isFeatureEnabled(KNOWLEDGE_BASE) ? [{ label: 'KBs', items: o.effective.knowledgeBases }] : []),
  ];

  return (
    <div className="space-y-6">
      <Link className="inline-flex items-center gap-1 text-sm text-brand-600 hover:underline" to="/users">
        <ArrowLeft className="h-3 w-3" /> Vissza
      </Link>

      <div className="card p-6">
        <h2 className="text-2xl font-semibold">{o.user.name}</h2>
        <p className="text-sm text-slate-600">{o.user.email}</p>
        <div className="mt-4 grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
          <div>
            <p className="text-xs uppercase text-slate-500">Department</p>
            <p>{o.user.department ?? '—'}</p>
          </div>
          <div>
            <p className="text-xs uppercase text-slate-500">Job title</p>
            <p>{o.user.jobTitle ?? '—'}</p>
          </div>
          <div>
            <p className="text-xs uppercase text-slate-500">Utolsó login</p>
            <p>{formatDate(o.user.lastLoginAt)}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="card p-4">
          <h3 className="mb-2 text-sm font-semibold">Szegmensek</h3>
          <ul className="space-y-1 text-sm">
            {o.segments.map((s) => (
              <li key={s.segmentId} className="flex items-center justify-between">
                <span>{s.segmentName}</span>
                <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{s.source}</span>
              </li>
            ))}
            {o.segments.length === 0 && <li className="text-slate-500">Nincs tagság.</li>}
          </ul>
        </div>
        <div className="card p-4">
          <h3 className="mb-3 text-sm font-semibold">Effective resources</h3>
          <div className="space-y-3">
            {effectiveCategories.map((c) => (
              <div key={c.label}>
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-xs font-medium uppercase text-slate-500">{c.label}</span>
                  <span className="text-xs text-slate-500">{c.items.length}</span>
                </div>
                {c.items.length === 0 ? (
                  <span className="text-xs text-slate-400">—</span>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {c.items.map((it) => (
                      <span key={it.id} className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                        {it.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="border-b border-slate-200 px-4 py-2 text-sm font-semibold">
          Utolsó használati események
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase">
            <tr>
              <th className="px-4 py-2 text-left">Időpont</th>
              <th className="px-4 py-2 text-right">Input tokens</th>
              <th className="px-4 py-2 text-right">Output tokens</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {o.recentUsage.map((u) => (
              <tr key={u.id}>
                <td className="px-4 py-2">{formatDate(u.occurredAt)}</td>
                <td className="px-4 py-2 text-right">{u.inputTokens}</td>
                <td className="px-4 py-2 text-right">{u.outputTokens}</td>
              </tr>
            ))}
            {o.recentUsage.length === 0 && (
              <tr>
                <td colSpan={3} className="px-4 py-4 text-center text-slate-500">
                  Nincs még használat.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
